import { AxiosResponse } from 'axios';
import { createCRUDHook } from './createCRUDHook';
import { prepareQueryParams } from 'utils';
import {
  ECGSiteDto,
  ECGSiteUserDto,
  FilterOptionModel,
  JsonPatch,
  RegionManagerDto,
} from 'utils/models';
import {
  CREATE_BULK_ENTITY,
  ECG_REGION_MANAGER,
  ECG_SITE,
  ECG_SITE_USER,
  FILTERED_ENTITIES,
  GET_ALL_SITE_DETAIL_WITH_PAGINATION,
} from './apiEndPoints';

interface ECGSiteCustomOperations {
  getAllSiteDetailWithPagination: (
    size: number,
    skip: number
  ) => Promise<ECGSiteDto[]>;

  addBulkEntityECGSiteData: (param: ECGSiteDto[]) => Promise<boolean>;
  // site users
  getECGSiteUserDataByFE: (
    filteredEntities: FilterOptionModel
  ) => Promise<ECGSiteUserDto[]>;
  addECGSiteUserData: (param: ECGSiteUserDto) => Promise<ECGSiteUserDto>;
  updateECGSiteUserData: (
    param: JsonPatch[],
    id: number
  ) => Promise<AxiosResponse>;
  deleteECGSiteUserData: (id: number) => Promise<boolean>;

  // regionManager
  getRegionManagerData: () => Promise<RegionManagerDto[]>;
  getRegionManagerDataById: (id: number) => Promise<RegionManagerDto>;
  getRegionManagerDataByFE: (
    filteredEntities: FilterOptionModel
  ) => Promise<RegionManagerDto[]>;
  addRegionManagerData: (param: RegionManagerDto) => Promise<RegionManagerDto>;
  updateRegionManagerData: (
    param: JsonPatch[],
    id: number
  ) => Promise<AxiosResponse>;
  deleteRegionManagerData: (id: number) => Promise<boolean>;
}

const { Context, useManager, useConsumer } = createCRUDHook<
  ECGSiteDto,
  ECGSiteDto,
  ECGSiteCustomOperations
>({
  entityName: 'ECGSite',
  endpoint: ECG_SITE,

  customOperations: (auth) => ({
    // ECG Site
    getAllSiteDetailWithPagination: async (
      size: number,
      skip: number
    ): Promise<ECGSiteDto[]> => {
      try {
        let res = await auth.makeAuthenticatedGet(
          ECG_SITE +
            GET_ALL_SITE_DETAIL_WITH_PAGINATION +
            `?pageSize=${size}&pageOffset=${skip}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    addBulkEntityECGSiteData: async (param: ECGSiteDto[]): Promise<boolean> => {
      try {
        let res = await auth.makeAuthenticatedPost(
          ECG_SITE + CREATE_BULK_ENTITY,
          param
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    // Site Users
    getECGSiteUserDataByFE: async (
      filteredEntities: FilterOptionModel
    ): Promise<ECGSiteUserDto[]> => {
      try {
        let res = await auth.makeAuthenticatedGet(
          ECG_SITE_USER +
            FILTERED_ENTITIES +
            `?${prepareQueryParams(filteredEntities)}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    addECGSiteUserData: async (
      param: ECGSiteUserDto
    ): Promise<ECGSiteUserDto> => {
      try {
        let res = await auth.makeAuthenticatedPost(ECG_SITE_USER, param);
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    updateECGSiteUserData: async (
      param: JsonPatch[],
      id: number
    ): Promise<AxiosResponse> => {
      try {
        let res = await auth.makeAuthenticatedPatch(
          `${ECG_SITE_USER}/${id}`,
          param
        );
        return Promise.resolve(res);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    deleteECGSiteUserData: async (id: number): Promise<boolean> => {
      try {
        const res = await auth.makeAuthenticatedDelete(
          `${ECG_SITE_USER}/${id}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return false;
      }
    },
    // Region Manager
    getRegionManagerData: async (): Promise<RegionManagerDto[]> => {
      try {
        let res = await auth.makeAuthenticatedGet(ECG_REGION_MANAGER);
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    getRegionManagerDataById: async (id: number): Promise<RegionManagerDto> => {
      try {
        let res = await auth.makeAuthenticatedGet(
          ECG_REGION_MANAGER + `/${id}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    getRegionManagerDataByFE: async (
      filteredEntities: FilterOptionModel
    ): Promise<RegionManagerDto[]> => {
      try {
        let res = await auth.makeAuthenticatedGet(
          ECG_REGION_MANAGER +
            FILTERED_ENTITIES +
            `?${prepareQueryParams(filteredEntities)}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    addRegionManagerData: async (
      param: RegionManagerDto
    ): Promise<RegionManagerDto> => {
      try {
        let res = await auth.makeAuthenticatedPost(ECG_REGION_MANAGER, param);
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    updateRegionManagerData: async (
      param: JsonPatch[],
      id: number
    ): Promise<AxiosResponse> => {
      try {
        let res = await auth.makeAuthenticatedPatch(
          ECG_REGION_MANAGER + `/${id}`,
          param
        );
        return Promise.resolve(res);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    deleteRegionManagerData: async (id: number): Promise<boolean> => {
      try {
        const res = await auth.makeAuthenticatedDelete(
          ECG_REGION_MANAGER + `/${id}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return false;
      }
    },
  }),
});

export const ECGSiteContext = Context;
export const useECGSiteManager = useManager;
export const useECGSite = useConsumer;
